import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormArray,
  AbstractControl,
  UntypedFormBuilder,
} from '@angular/forms';
import {
  MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent,
  MatLegacyAutocompleteTrigger as MatAutocompleteTrigger,
} from '@angular/material/legacy-autocomplete';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// ANIMATIONS
import { IClientCreditMemo, IClientPurchaseOrderDiscount } from '../../../client';
import { ESalesOrderStatus } from '../../../order';
import { slideOutAnimation } from '../../../shared/animations';
import { DialogConfig } from '../../../shared/configs';
import { IColumnConfig } from '../../../shared/interfaces/table.interface';
import { trackByControlValue, trackById } from '../../../shared/util';

// COMPONENT
import { ShippingRatesComponent } from '../../../shipping';
import { UiMessageDialogComponent } from '../../../ui/components/ui-message-dialog/ui-message-dialog.component';

//INTERFACES
import { ELineItemRowType, IInvoice } from '../../interfaces/invoice.interface';

@Component({
  selector: 'lum-invoice-skus-table',
  templateUrl: './invoice-skus-table.component.html',
  styleUrls: ['./invoice-skus-table.component.scss'],
  animations: [slideOutAnimation],
})
export class InvoiceSkusTableComponent implements OnInit, OnDestroy {
  @Input()
  form :UntypedFormGroup;

  @Input()
  discountEntries :IClientPurchaseOrderDiscount[] = [];

  @Input()
  creditMemos :IClientCreditMemo[] = [];

  @Input()
  loadingCreditMemos :boolean;

  @Input()
  currentInvoice :IInvoice;

  @Input()
  discountAmount :number = 0;

  @Input()
  templateId :string;

  @Input()
  searchableFields :string[] = [];

  @Input()
  searchStr :string = '';

  @Input()
  rfqs :any[] = []; // TODO: Create interface

  @Input()
  columns :IColumnConfig[] = [];

  @Input()
  loadingRfqs :boolean = false;

  @Input()
  fromEnterEvent :boolean = false;

  @Input()
  isInvoiceStatusComplete :boolean = false;

  @Output()
  removeLineItem = new EventEmitter<number>();

  @Output()
  removeCreditMemoEntry = new EventEmitter<number>();

  @Output()
  addRowItems = new EventEmitter<number>();

  @Output()
  addRowItemSubTotal = new EventEmitter<number | null>();

  @Output()
  addInvoiceCreditMemoEntry = new EventEmitter<UntypedFormGroup>();

  @Output()
  searchRfq = new EventEmitter<any>(); // TODO: Create interface

  @Output()
  enterRfqEvent = new EventEmitter<{
    item :AbstractControl; // TODO: Create interface
    index :number;
    searchStr :string;
  }>();

  @Output()
  selectRfq = new EventEmitter<{
    item :any; // TODO: Create interface
    tempId :number;
  }>();

  @Output()
  selectCreditMemo = new EventEmitter<{
    creditMemo :IClientCreditMemo;
    index :number;
  }>();

  @Output()
  scrollPaginate = new EventEmitter<boolean>();

  @Output()
  recalculateSubtotals = new EventEmitter();

  @ViewChild('inputRef')
  inputRef! :ElementRef;

  @ViewChild(MatAutocompleteTrigger)
  autoCompleteTrigger! :MatAutocompleteTrigger;

  filteredCreditMemos :IClientCreditMemo[] = [];

  trackByControlValue = trackByControlValue;
  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private dialog :MatDialog,
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
  ) {}

  ngOnInit() :void {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  trackClientDiscountTypeId(index :number, item :IClientPurchaseOrderDiscount) {
    return item.discountTypeId;
  }

  onRemoveCreditMemoEntry(index :number) :void {
    this.removeCreditMemoEntry.emit(index);
  }

  onOpenShippingRatesDialog() :void {
    this.dialog
      .open(ShippingRatesComponent, {
        data: {
          toPostalCode: this.form.get('ship_zip')?.value,
          toCountry: this.form.get('ship_country')?.value,
          toState: this.form.get('ship_state')?.value,
          toCity: this.form.get('ship_city')?.value,
        },
        width: DialogConfig.sizes.medium,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((rate :any) => {
        if (rate) {
          this.form.patchValue({
            shipping_amount: rate.shipmentCost,
          });

          this.cd.detectChanges();
        }
      });
  }

  onSelectCreditMemo(event :MatAutocompleteSelectedEvent, index :number) :void {
    this.selectCreditMemo.emit({
      creditMemo: event.option.value,
      index: index,
    });
    this.getFilteredCreditMemo(null);
  }

  displayFnCreditMemo(option :any) :string {
    return option && option.label ? `${option.label}` : '';
  }

  onAddInvoiceCreditMemoEntry() :void {
    this.addInvoiceCreditMemoEntry.emit(this.emptyInvoiceCreditMemo);
  }

  onSearchCreditMemo(event :any) :void {
    this.getFilteredCreditMemo(event);
  }

  toggleCreditMemoNote() :void {
    this.dialog.open(UiMessageDialogComponent, {
      data: {
        title: 'Credit Memo Note',
        message:
          'Credit memos are treated as payment, the total credit memo amount will be deducted after saving this Invoice.',
      },
      width: DialogConfig.sizes.small,
      disableClose: true,
    });
  }

  private getFilteredCreditMemo(event :any) :void {
    const searchKey :string = event?.data ?? '';
    const creditMemoEntryControl :number[] =
      this.invoiceCreditMemoFormArray.controls.map(
        (creditMemoEntry) => creditMemoEntry.value.creditMemo.id,
      );
    this.filteredCreditMemos = this.creditMemos.filter((creditMemo) => {
      return (
        creditMemo.label.toLowerCase().includes(searchKey.toLowerCase()) &&
        !creditMemoEntryControl?.includes(creditMemo.id)
      );
    });
  }

  get subTotal() :number {
    return this.form.get('total_amount')?.value;
  }

  get netTotal() :number {
    return this.form.get('net_total_amount')?.value || 0;
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form?.get('line_items') as UntypedFormArray;
  }

  get isEditInvoice() :boolean {
    //returned like this because this.currentInvoice.length > 0 is not accurate
    return this.currentInvoice?.id && this.templateId === '' ? true : false;
  }

  get isSalesOrderShippedStatus() :boolean {
    const salesOrderStatus = this.currentInvoice?.sales_order?.orderStatus || null;

    return salesOrderStatus === ESalesOrderStatus.SHIPPED;
  }

  get emptyInvoiceCreditMemo() :UntypedFormGroup {
    return this.fb.group({
      creditMemo: null,
      amount: 0,
      applyCreditMemoEntry: false,
    });
  }

  get invoiceCreditMemoFormArray() :UntypedFormArray {
    return this.form.get('creditMemoEntries') as UntypedFormArray;
  }

  get totalCreditMemoEntryAmount() :number {
    return this.invoiceCreditMemoFormArray.value
      .map((value :{ amount :number }) => value.amount)
      .reduce((acc :number, curr :number) => acc + curr, 0);
  }

  get totalDiscount() :number {
    return this.lineItemsFormArray?.value
      ?.filter((item :any) => item.row_type !== ELineItemRowType.SUB_TOTAL)
      .reduce((acc :number, curr :any) => acc + Number(curr.discount_amount), 0);
  }

  get totalUnits() :number {
    return this.lineItemsFormArray?.value
      ?.filter((item :any) => item.row_type !== ELineItemRowType.SUB_TOTAL)
      .reduce((acc :number, curr :any) => acc + Number(curr.quantity), 0);
  }
}
